import * as React from 'react';
import { extractFormCellProps, FormCell, IFormCellProps } from '../FormCell';
import { InputIconStyled, InputStyled } from './Input.styles';
import { mergeTests } from '../../test-utilities/mergeTests';
import { IconSize, IconType } from '../Icon';
import styled, { ThemeContext } from 'styled-components';
import { RotatingIcon } from '../Spinner';
import { useEffect, useRef, useState } from 'react';
import isNil from 'lodash/isNil';
import useDebounce from '../../../utilities/hooks/useDebounce';

export interface IInputIconProps {
  iconType?: IconType;
  anchor: 'left' | 'right';
}

const InputIcon: React.FC<IInputIconProps> = ({ anchor, iconType }) => {
  const theme = React.useContext(ThemeContext);
  return !!iconType ? (
    <InputIconStyled
      anchor={anchor}
      type={iconType}
      size={IconSize.Small}
      color={theme.colors.defaultContentText}
      as={iconType === IconType.Loading ? RotatingIcon : undefined}
    />
  ) : null;
};

const InputWrapper = styled.div`
  position: relative;
`;

export interface ITextInputProps extends IFormCellProps {
  /** The value attribute of input. */
  value?: string | null;
  /** Specifies is the textarea should automatically get focus. It also works after initializing the component using the imperative approach 'false -> true'. */
  autoFocus?: boolean;
  /** Additional form CSS class. */
  className?: string;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** A callback triggered when component is focused. */
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  /** A callback fired when the value prop changes. */
  onChange?: (option: string | null, error?: string | null) => void;
  /** A callback fired when the user copies. */
  onCopy?: React.ClipboardEventHandler<HTMLInputElement>;
  /** Append this icon type */
  appendIconType?: IconType;
  /** Prepend this icon type */
  prependIconType?: IconType;
  /** Switches the input to read only which prevents editing the contents. */
  readOnly?: boolean;
  /** This callback exposes the input reference / DOM node to parent components. */
  getInputRef?: (elem: HTMLInputElement) => void;
  debounceOnValueChange?: boolean;
}

/**
 * The HTML input with a label and error messaging.
 */
export const TextInput: React.FC<ITextInputProps> = (props) => {
  const [value, setValue] = useState<string>(props.value || '');
  const [focused, setFocused] = useState<boolean>(!!props.autoFocus);

  const textInput = useRef(null);
  useEffect(() => {
    if (props.getInputRef && textInput?.current) {
      props.getInputRef(textInput.current);
    }
    // eslint-disable-next-line
  }, [textInput, props.getInputRef]);

  const doAutoFocus = (autofocus: boolean) => {
    if (autofocus && textInput) {
      textInput.current && (textInput.current as HTMLInputElement).focus();
      setFocused(true);
    }
  };

  useEffect(() => {
    doAutoFocus(props.autoFocus || false);
  }, [props.autoFocus]);

  useEffect(() => {
    if (props.value !== value && (!props.debounceOnValueChange || focused)) {
      setValue(props.value || '');
    }
    // eslint-disable-next-line
  }, [props.value, props.debounceOnValueChange]);
  const updateValue = useDebounce(value, 500);

  useEffect(() => {
    const valueToSave = updateValue === '' ? null : updateValue;
    if (props.debounceOnValueChange && props.onChange && valueToSave !== props.value) {
      if (!isNil(valueToSave) || !isNil(props.value)) {
        props.onChange(valueToSave);
      }
    }
    // eslint-disable-next-line
  }, [updateValue]);

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? null : e.target.value;
    const error = props.isRequired && value === null ? `${props.label} cannot be blank` : null;
    setValue(e.target.value);
    if (!props.debounceOnValueChange && props.onChange) {
      props.onChange(value, error);
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const { formCellProps, inputProps } = extractFormCellProps({ currentCharacterCount: value.length, ...props });
  const { 'data-test-id': dataTestID, prependIconType, appendIconType } = inputProps;
  const dataTestClass = inputProps['data-test-class'] || 'text-input';
  return (
    <FormCell {...formCellProps} data-test-id={dataTestID} data-test-class={dataTestClass}>
      <InputWrapper>
        <InputIcon
          anchor={'left'}
          iconType={prependIconType}
          data-test-class={mergeTests(dataTestClass, 'prepend-icon')}
        />
        <InputIcon
          anchor={'right'}
          iconType={appendIconType}
          data-test-class={mergeTests(dataTestClass, 'append-icon')}
        />
        <InputStyled
          {...inputProps}
          hasPrependIcon={!!prependIconType}
          hasAppendIcon={!!appendIconType}
          type={'text'}
          showErrorHighlight={!!formCellProps.errorText}
          ref={textInput}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          onCopy={props.onCopy}
          value={value}
          data-test-id={mergeTests(dataTestID, 'input')}
          data-test-class={mergeTests(dataTestClass, 'input')}
          disabled={formCellProps.disabled}
        />
      </InputWrapper>
    </FormCell>
  );
};
